import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import classes from '../SettingsElements.module.scss'

import { Button, ButtonText, FormField, Text, TextInput, useToast } from '@percent/lemonade'
import { useAuth, useMutation } from '@percent/workplace-giving/common/hooks'
import {
  getAccountFromAuthState,
  getGivingProfileFromAuthState
} from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import secondaryEmailClasses from './SecondaryEmail.module.scss'
import { useSharedValidationRules } from '@percent/workplace-giving/common/hooks/useSharedValidationRules/useSharedValidationRules'
import { updateOrRemoveGivingProfileSecondaryEmail } from '@percent/workplace-giving/api/account/updateOrRemoveGivingProfileSecondaryEmail/updateOrRemoveGivingProfileSecondaryEmail'
import { okResponse } from '@percent/workplace-giving/api/goodstack'

enum ActionType {
  UPDATE = 'update',
  REMOVE = 'remove'
}
export function SecondaryEmail() {
  const { t } = useTranslation()
  const { addToast } = useToast()
  const { state, dispatch } = useAuth()
  const account = getAccountFromAuthState(state)!
  const givingProfile = getGivingProfileFromAuthState(state)!
  const { validateEmail } = useSharedValidationRules()
  const [editMode, setIsEditMode] = useState(false)

  const turnEditModeOn = () => {
    setIsEditMode(true)
  }

  const successMessages: Record<ActionType, string> = useMemo(
    () => ({
      [ActionType.UPDATE]: t('workplace_giving.settings.accountDetails.secondaryEmail.update.success'),
      [ActionType.REMOVE]: t('workplace_giving.settings.accountDetails.secondaryEmail.remove.success')
    }),
    [t]
  )

  const errorMessages: Record<ActionType, string> = useMemo(
    () => ({
      [ActionType.UPDATE]: t('workplace_giving.settings.accountDetails.secondaryEmail.update.error'),
      [ActionType.REMOVE]: t('workplace_giving.settings.accountDetails.secondaryEmail.remove.error')
    }),
    [t]
  )

  const handleSuccess = (action: ActionType) => {
    addToast(successMessages[action], 'success')

    setIsEditMode(false)
  }

  const handleError = (action: ActionType) => {
    addToast(errorMessages[action], 'success')
  }

  const { mutateAsync } = useMutation({
    mutationFn: updateOrRemoveGivingProfileSecondaryEmail,
    onSuccess: res => {
      try {
        if (okResponse(res)) {
          dispatch({
            type: 'UPDATE_GIVING_PROFILE',
            payload: {
              givingProfile: {
                ...givingProfile,
                secondaryEmail: res.data.secondaryEmail
              }
            }
          })
        }
      } catch {
        addToast(t('workplace_giving.errors.unexpected'), 'error')
      }
    }
  })

  const { isValid, dirty, errors, values, handleChange, handleSubmit, handleBlur, touched, isSubmitting, resetForm } =
    useFormik({
      initialValues: {
        secondaryEmail: givingProfile?.secondaryEmail || ''
      },
      validationSchema: () =>
        Yup.object().shape({
          secondaryEmail: validateEmail(false)
            .trim()
            .test(
              'secondaryEmailDifferentThanWorkEmail',
              t('workplace_giving.validation.secondaryEmailSameAsWorkEmail'),
              (value: string | null | undefined) => {
                return value !== account?.email
              }
            )
        }),
      onSubmit: async ({ secondaryEmail }) => {
        try {
          await mutateAsync({
            secondaryEmail: secondaryEmail.trim()
          })
          handleSuccess(ActionType.UPDATE)
        } catch {
          handleError(ActionType.UPDATE)
        }
      }
    })

  const saveBtnEnabled = isValid && dirty

  const updateSecondaryEmail = () => {
    handleSubmit()
  }

  const removeSecondaryEmail = async () => {
    try {
      await mutateAsync({
        secondaryEmail: null
      })
      handleSuccess(ActionType.REMOVE)
      resetForm()
    } catch {
      handleError(ActionType.REMOVE)
    }
  }

  return (
    <div className={classes.SettingsElementCard}>
      <Text className={classes.SettingsElementTitle} fontWeight="semiBold" size="large" as="p">
        {t('workplace_giving.settings.accountDetails.title')}
      </Text>
      <div className={classes.SettingsSection}>
        <Text className={classes.SettingsElementDescription} as="p" size="small" fontWeight="medium">
          {t('workplace_giving.settings.accountDetails.workEmail.title')}
        </Text>
        <Text className={classes.SettingsElementDescription} as="p" size="small">
          {account.email}
        </Text>
        <Text className={classes.SettingsElementDescription} as="p" size="small" color="gray600">
          {t('workplace_giving.settings.accountDetails.workEmail.description')}
        </Text>
      </div>
      <div className={classes.SettingsSection}>
        <Text className={classes.SettingsElementDescription} as="p" size="small" fontWeight="medium">
          {t('workplace_giving.onboardingPopup.label.secondaryEmail')}
        </Text>
        {!givingProfile?.secondaryEmail && !editMode ? (
          <ButtonText onClick={turnEditModeOn}>
            {t('workplace_giving.settings.accountDetails.secondaryEmail.addEmail')}
          </ButtonText>
        ) : null}
        {givingProfile?.secondaryEmail && !editMode ? (
          <div className={secondaryEmailClasses.OptionsWrapper}>
            <Text className={classes.SettingsElementDescription} as="p" size="small">
              {givingProfile?.secondaryEmail}
            </Text>
            <div className={secondaryEmailClasses.FlexWrapper}>
              <ButtonText onClick={turnEditModeOn}>{t('workplace_giving.common.edit')}</ButtonText>
              <ButtonText onClick={removeSecondaryEmail}>{t('workplace_giving.common.remove')}</ButtonText>
            </div>
          </div>
        ) : null}
        {editMode ? (
          <div className={secondaryEmailClasses.SettingsWrapper}>
            <FormField
              status={touched.secondaryEmail && errors.secondaryEmail ? 'danger' : 'default'}
              statusMessage={errors.secondaryEmail}
              data-testid="secondaryEmail"
              necessity="required"
              disabled={isSubmitting}
            >
              <TextInput
                name="secondaryEmail"
                data-testid="secondaryEmail"
                value={values.secondaryEmail}
                placeholder={t('workplace_giving.onboardingPopup.placeholder.secondaryEmail')}
                onBlur={handleBlur}
                onChange={handleChange}
                borderRadiiSize="large"
              />
            </FormField>
            <div className={secondaryEmailClasses.FlexWrapper}>
              <Button
                data-testid="saveSecondaryEmailBtn"
                shape="oval"
                onClick={updateSecondaryEmail}
                disabled={isSubmitting || !saveBtnEnabled}
              >
                {t('workplace_giving.common.save')}
              </Button>
              <ButtonText
                onClick={() => {
                  setIsEditMode(false)
                  resetForm()
                }}
                disabled={isSubmitting}
              >
                {t('workplace_giving.common.cancel')}
              </ButtonText>
            </div>
          </div>
        ) : null}
        <Text className={classes.SettingsElementDescription} as="p" size="small" color="gray600">
          {t('workplace_giving.onboardingPopup.input.description.secondaryEmail')}
        </Text>
      </div>
    </div>
  )
}
